/**
 * Feature copy. Every entry below describes capability that exists in the
 * product today (flass-backend routes/services, flass-frontend, flass-native) —
 * keep it that way when editing, and move anything unreleased to the roadmap
 * rather than listing it here.
 */
const FEATURE_GROUPS = [
	{
		eyebrow: 'Messaging',
		title: 'Every conversation your campus needs',
		subtitle:
			'Course-wide announcements, study groups, and one-to-one chats live in one place, so students and staff stop juggling email threads and third-party group chats.',
		features: [
			{
				title: 'Course rooms and groups',
				text:
					'Give every module, cohort, and society its own room. Staff can pin the rooms that matter, archive the ones that are finished, and bulk-create rooms for an entire intake instead of setting them up one by one.',
			},
			{
				title: 'Direct and group messages',
				text:
					'Students and staff message each other directly, with typing indicators, online presence, and read state so nobody is left guessing whether a message landed.',
			},
			{
				title: 'Anonymous rooms',
				text:
					'Run sensitive discussions — feedback, wellbeing, peer support — where participants appear under consistent pseudonyms instead of their real names.',
			},
			{
				title: 'Replies, edits, and forwarding',
				text:
					'Reply in context, correct a message after sending, and forward anything to another room while keeping a clear record of where it came from.',
			},
			{
				title: 'Mentions and drafts',
				text:
					'Pull someone in with @name, or reach the whole room with @here. Unsent drafts follow you between devices, so a half-written reply is never lost.',
			},
			{
				title: 'Scheduled messages',
				text:
					'Write an announcement now and have it delivered at the right moment — the morning of a deadline, or the start of term.',
			},
		],
	},
	{
		eyebrow: 'Teaching and engagement',
		title: 'Tools built for how universities actually work',
		subtitle:
			'Flass goes past chat with the workflows staff run every week, so timetabled support and student feedback happen where the conversation already is.',
		features: [
			{
				title: 'Virtual office hours',
				text:
					'Open a session and students join a queue. Staff take the next student in order, everyone can see where they are in the line, and the session closes when the slot ends.',
			},
			{
				title: 'Events',
				text:
					'Publish lectures, workshops, and society events. Students browse what is on, register to attend, and organisers see the attendee list as it fills.',
			},
			{
				title: 'Polls',
				text:
					'Check understanding in seconds with a poll inside the room, then open the results to the group when you are ready.',
			},
			{
				title: 'Surveys',
				text:
					'Collect structured module feedback with multi-question surveys and review the responses without exporting anything to a separate tool.',
			},
			{
				title: 'Pinned and saved messages',
				text:
					'Pin the reading list or deadline to the top of a room for everyone, and let each person privately save the messages they need to come back to.',
			},
			{
				title: 'Search across your messages',
				text:
					'Find any message, in any room, without giving up privacy — search runs over encrypted keyword tokens rather than readable message text.',
			},
		],
	},
	{
		eyebrow: 'Campus life',
		title: 'A student community, not just a chat app',
		subtitle:
			'Discovery features that keep students on campus services instead of unmoderated external groups.',
		features: [
			{
				title: 'Marketplace',
				text:
					'A student-to-student marketplace for textbooks, equipment, and housing, with search, tag filters, and recommendations tuned to what each student is browsing.',
			},
			{
				title: 'Jobs board',
				text:
					'Post part-time roles, internships, and research assistantships. Students filter by tag and see featured listings from the university first.',
			},
			{
				title: 'Public groups and events',
				text:
					'Societies run open groups and public events that any student can find and join, so joining campus life does not depend on knowing the right person.',
			},
		],
	},
	{
		eyebrow: 'Files and media',
		title: 'Share the material, not the link to the material',
		subtitle:
			'Lecture slides, recordings, and reading move through Flass directly, with previews that work without leaving the conversation.',
		features: [
			{
				title: 'Documents and PDFs',
				text:
					'Open PDFs, Word documents, and spreadsheets inline. Nothing has to be downloaded first just to check whether it is the right file.',
			},
			{
				title: 'Images, video, and audio',
				text:
					'View images full screen with pinch-to-zoom, play video inline, and keep listening to a lecture recording while you carry on reading the room.',
			},
			{
				title: 'Voice and video calls',
				text:
					'Move from a message to a live voice or video call in the browser when a conversation needs a face rather than a thread. Part of the higher of our two plans.',
			},
			{
				title: 'Downloads and storage',
				text:
					'Everything shared with you stays in one downloads view, and students can see and clear what Flass is storing on their device.',
			},
		],
	},
	{
		eyebrow: 'Security and administration',
		title: 'Built for an institution, not a group chat',
		subtitle:
			'Universities handle personal data and pastoral conversations. Flass is designed on that assumption.',
		features: [
			{
				title: 'End-to-end encryption',
				text:
					'Messages are encrypted with AES-256-GCM under per-room keys that are exchanged using RSA and only ever unwrapped on a signed-in device. Message contents are not readable on the server.',
			},
			{
				title: 'Encrypted notifications',
				text:
					'Push notification previews are encrypted too, so a message on a lock screen is readable by the recipient and nobody in between.',
			},
			{
				title: 'University sign-in',
				text:
					'Students and staff sign in with their university email — no separate account to create, and no separate account to deprovision when someone leaves.',
			},
			{
				title: 'Roles and moderation',
				text:
					'Room owners and administrators control membership, invitations, and whether a room accepts messages at all, with clear badges showing who holds which role.',
			},
			{
				title: 'Time-limited rooms',
				text:
					'Set a room to expire at the end of a module or an event. It closes to new messages automatically instead of lingering as a stale channel.',
			},
			{
				title: 'Notification control',
				text:
					'Per-room notification settings mean a student can stay reachable for their tutor without being woken by a society group at 2am.',
			},
		],
	},
];

export default FEATURE_GROUPS;
